import AddIcon from '@mui/icons-material/Add';
import { Button, Divider } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';

import { Service } from 'common/src/types/enums/Service.enum';
import { IFacility, IMonthlyDetail } from 'common/src/types/interfaces';

import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';
import { ValidationError } from 'portal/src/utils/ValidationError';
import { ValidateGuard } from 'portal/src/validators/validateGuard';

import { getColumns } from './AddFacilityForm.fields';
import { AddFacilityFormValidators } from './AddFacilityForm.validators';

export const SERVICE_TYPES = Object.values(Service);

export type FacilityServiceRow = {
  id: string;
  serviceType: Service;
  cost: number;
};

type FacilityFormValues = {
  facilityId?: string;
  serviceTypes: FacilityServiceRow[];
};

type FacilityRowPayload = Omit<FacilityServiceRow, 'id'> & {
  facility?: Partial<IFacility>;
  facilityId?: string;
  month: number;
};

const normalizeRows = (rows: IMonthlyDetail[] = []): FacilityServiceRow[] =>
  rows.map(({ serviceType, cost }) => ({
    id: serviceType,
    serviceType,
    cost,
  }));

export const AddFacilityForm = ({
  rows: rowsProp = [],
  facilityId = null,
  month,
  open,
  facilities,
  onSave,
  onClose,
}: {
  rows?: IMonthlyDetail[];
  facilityId?: string;
  facilities: IFacility[];
  month: number;
  open: boolean;
  onClose: () => void;
  onSave: (data: FacilityRowPayload[]) => void;
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const snackbarHandler = useSnackbarHandler();
  const isReadonly = hasReadOnlyAccess();

  const {
    control,
    formState: { isDirty },
    setValue,
    handleSubmit,
    reset,
  } = useForm<FacilityFormValues>({
    defaultValues: {
      facilityId,
      serviceTypes: normalizeRows(rowsProp),
    },
  });

  const rows = useWatch({ control, name: 'serviceTypes' }) ?? [];

  const handleDeleteServiceType = (id: string) => {
    setValue(
      'serviceTypes',
      rows.filter((row) => row.id !== id),
      { shouldDirty: true },
    );
  };

  const usedServiceTypes = rows.map(({ serviceType }) => serviceType);
  const availableServiceTypes = SERVICE_TYPES.filter(
    (service) => !usedServiceTypes.includes(service),
  );

  const formatData = ({
    facilityId,
    serviceTypes,
  }: FacilityFormValues): FacilityRowPayload[] => {
    const facility = facilities.find((f) => f.id === facilityId);
    return serviceTypes.map(({ id, ...r }) => ({
      facility,
      facilityId,
      month,
      ...r,
    }));
  };

  const resetState = () => {
    reset({ facilityId, serviceTypes: normalizeRows(rowsProp) });
  };

  const handleSaveWithValidation = async (formData: FacilityFormValues) => {
    try {
      const validator = new AddFacilityFormValidators(() => formData);
      await ValidateGuard.validate(validator.getServiceTypesValidator());

      const formattedData = formatData(formData);
      onSave(formattedData);

      // If this is a new facility for the month, reset the default values so we can add another
      if (!facilityId) {
        resetState();
      }
    } catch (err: any) {
      if (err instanceof ValidationError) {
        snackbarHandler.error(err.errors.join('; \n'));
      } else {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      }
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const addNewRow = () => {
    if (availableServiceTypes.length === 0) return;

    const serviceType = availableServiceTypes[0];
    const newId = `${Date.now()}-${serviceType}`;

    const newRow = {
      id: newId,
      serviceType,
      cost: 0,
    };

    setValue('serviceTypes', [...rows, newRow], { shouldDirty: true });
  };

  const handleProcessRowUpdate = (
    updatedRow: FacilityServiceRow,
    originalRow: FacilityServiceRow,
  ) => {
    const isValidServiceType = SERVICE_TYPES.includes(updatedRow.serviceType);
    const isDuplicateServiceType = rows.some(
      (row) =>
        row.id !== updatedRow.id && row.serviceType === updatedRow.serviceType,
    );
    const isValidCost = !isNaN(updatedRow.cost) && updatedRow.cost >= 0;

    if (!isValidServiceType || isDuplicateServiceType || !isValidCost) {
      return originalRow;
    }

    setValue(
      'serviceTypes',
      rows.map((row) => (row.id === updatedRow.id ? updatedRow : row)),
      { shouldDirty: true },
    );

    return updatedRow;
  };

  const isAddButtonDisabled = isReadonly || availableServiceTypes.length === 0;

  return (
    <GenericDialog open={open} onClose={handleClose}>
      <FormContainer
        control={control}
        header={`${facilityId ? 'Edit' : 'Add'} Facility`}
        isDirty={isDirty}
        isNew={!facilityId}
        isPending={false}
        mainFieldsConfig={[
          {
            disabled: !!facilityId,
            label: 'Facility',
            placeholder: 'Select a facility',
            name: 'facilityId',
            values: facilities.map(({ id: key, name, isActive }) => ({
              disabled: !isActive,
              label: name,
              value: key,
              key,
            })),
            type: 'selectInput',
            required: true,
          },
        ]}
        handleBackAction={handleClose}
        handleSubmit={handleSubmit((data) => handleSaveWithValidation(data))}
        afterMainFields={
          <>
            <Divider>Service Types</Divider>
            <DataTable
              actionsConfig={{
                inRowEditConfig: {
                  processRowUpdate: handleProcessRowUpdate,
                  btnConfig: (row) => ({
                    onDeleteClick: () => handleDeleteServiceType(row.id),
                  }),
                },
              }}
              checkboxSelection={false}
              columns={getColumns()}
              getRowId={(row) => row.id ?? row.serviceType}
              hideFooter
              isRowNavigation={false}
              rows={rows}
              toolbarSlotProps={{
                children: (
                  <Button
                    color="secondary"
                    disabled={isAddButtonDisabled}
                    onClick={addNewRow}
                    startIcon={<AddIcon />}
                    size="small"
                    variant="outlined"
                  >
                    Add service type ({availableServiceTypes.length})
                  </Button>
                ),
                showFilters: false,
              }}
              noDataDisplay="No service types added yet. Use the button above to add a service type."
            />
          </>
        }
      />
    </GenericDialog>
  );
};
